/**
 * Source: wishlist half of the product-card adapter layer.
 *
 * Same role as webshop_source.js's WebshopSource, for the /wishlist page:
 * raftor_webhq.catalog.wishlist_listing.get_wishlist_listing_rows already
 * returns rows reshaped into the listing-row contract adaptItem() expects
 * (route/item_code/website_image/has_variants/formatted_price/wished, ...),
 * plus its own settings envelope - so adaptItems() (webshop.js) is reused
 * as-is, same as WebshopSource and RecommendationSource both do. Runs the
 * same decoration batch those two do (swatches/sizes/badges/offers/media/
 * ratings) so a wished card looks and behaves identically to the same
 * product's card on /all-products - a product card must render the same
 * everywhere it appears, regardless of which source fetched it.
 */

import { adaptItems } from './webshop.js';
import { fetchDecorations, routesAndCodes } from './webshop_source.js';
import { getSwatchColorOverrides } from '../swatch_color_overrides.js';
import { getAttributeIcons } from '../attribute_icons.js';

const METHOD = 'raftor_webhq.catalog.wishlist_listing.get_wishlist_listing_rows';

/**
 * Promise-wrapped frappe.call guaranteed to settle even if the server throws
 * and frappe.call invokes neither callback nor error - same documented
 * failure mode webshop_source.js's call() guards against.
 */
function call(method, args) {
	return new Promise((resolve) => {
		let settled = false;
		const done = (value) => {
			if (settled) return;
			settled = true;
			resolve(value);
		};

		if (typeof frappe === 'undefined' || !frappe.call) {
			done(null);
			return;
		}

		let req;
		try {
			req = frappe.call({ method, args, callback: (r) => done(r && r.message), error: () => done(null) });
		} catch (err) {
			done(null);
			return;
		}

		if (req && typeof req.always === 'function') {
			req.always(() => setTimeout(() => done(null), 0));
		} else {
			setTimeout(() => done(null), 15000);
		}
	});
}

export class WishlistSource {
	/**
	 * @param {object} [opts]
	 * @param {CartStore} [opts.cartStore] forwarded into fetchDecorations() for
	 *        per-card cart qty, same as WebshopSource.
	 */
	constructor(opts) {
		this.cartStore = (opts || {}).cartStore || null;
	}

	/**
	 * Not paginated - a wishlist is small enough to fetch whole. Any start > 0
	 * (the paginator's own retry/scroll path) short-circuits to empty rather
	 * than re-fetching the same full list.
	 * @returns {Promise<{models, settings, pagination}>}
	 */
	async fetch(query) {
		const start = (query && query.start) || 0;
		if (start > 0) {
			return { models: [], settings: {}, pagination: { hasMore: false } };
		}

		const [result, swatchColorOverrides, attributeIcons] = await Promise.all([call(METHOD, {}), getSwatchColorOverrides(), getAttributeIcons()]);
		const items = (result && result.items) || [];
		const settings = (result && result.settings) || {};
		settings.swatchColorOverrides = swatchColorOverrides;
		settings.attributeIcons = attributeIcons;

		const { routes, itemCodes } = routesAndCodes(items);
		const decorations = await fetchDecorations(routes, itemCodes, items, this.cartStore);

		return {
			models: adaptItems(items, settings, decorations),
			settings,
			pagination: { hasMore: false, totalCount: items.length, countReliable: true },
		};
	}
}
